import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get all features
 * @returns {Promise<Array>} Array of all features
 */
export const getAllFeatures = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching all features:', error);
    throw error;
  }
};

/**
 * Get feature by ID
 * @param {string} featureId - The feature ID
 * @returns {Promise<Object>} Feature data
 */
export const getFeature = async (featureId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching feature:', error);
    throw error;
  }
};

/**
 * Create a new feature
 * @param {Object} featureData - The feature data
 * @returns {Promise<Object>} Created feature data
 */
export const createFeature = async (featureData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(featureData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating feature:', error);
    throw error;
  }
};

/**
 * Update an existing feature
 * @param {string} featureId - The feature ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} Updated feature data
 */
export const updateFeature = async (featureId, updateData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating feature:', error);
    throw error;
  }
};

/**
 * Delete a feature
 * @param {string} featureId - The feature ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteFeature = async (featureId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting feature:', error);
    throw error;
  }
};

/**
 * Get feature configurations for a specific feature
 * @param {string} featureId - The feature ID
 * @returns {Promise<Array>} Array of facility IDs where the feature is configured
 */
export const getFeatureConfigurations = async (featureId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}/configurations`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.facilityIds || [];
  } catch (error) {
    console.error('Error fetching feature configurations:', error);
    throw error;
  }
};

/**
 * Update feature configurations for a specific feature
 * @param {string} featureId - The feature ID
 * @param {Array} facilityIds - Array of facility IDs to enable the feature for
 * @returns {Promise<Object>} Update result
 */
export const updateFeatureConfigurations = async (featureId, facilityIds) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}/configurations`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ facilityIds })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating feature configurations:', error);
    throw error;
  }
};

/**
 * Check if a feature is enabled for a specific facility
 * @param {string} featureId - The feature ID
 * @param {string} facilityId - The facility ID
 * @returns {Promise<boolean>} True if feature is enabled, false otherwise
 */
export const isFeatureEnabled = async (featureId, facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/features/${featureId}/enabled/${facilityId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.enabled || false;
  } catch (error) {
    console.error('Error checking feature status:', error);
    throw error;
  }
};

/**
 * Get all enabled features for a specific facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Array of enabled feature IDs
 */
export const getEnabledFeaturesForFacility = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/facilities/${facilityId}/enabled-features`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.featureIds || [];
  } catch (error) {
    console.error('Error fetching enabled features for facility:', error);
    throw error;
  }
};
